/* ============================================================
   MM-PERSONA-QUIZ (rev 2) - Mae & Mini email capture popup
   3-step quiz: persona pick -> email -> discount code reveal.
   Klaviyo profile property `persona` enables per-persona flows.
   ============================================================ */
(function () {
  'use strict';

  // ----- Config -----
  const STORAGE_SEEN = 'mm_persona_quiz_seen_at';
  const STORAGE_COMPLETED = 'mm_persona_quiz_completed';
  const STORAGE_DISMISSED = 'mm_persona_dismissed';
  const cfg = (window.MMWishlistConfig || {});
  const quizConfig = window.MMPersonaQuizConfig || {};
  const KLAVIYO_KEY = cfg.klaviyoPublicKey || 'JGdri9';
  const KLAVIYO_LIST_ID = quizConfig.listId || '';
  const DISCOUNT_CODE = quizConfig.discountCode || 'MAE10MINI';
  const DISCOUNT_PERCENT = quizConfig.discountPercent || 10;

  // ----- Helpers -----
  function readStorage(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }
  function writeStorage(key, value) { try { localStorage.setItem(key, value); } catch (e) {} }

  function track(eventName, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, params || {});
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
      }
      if (typeof window.clarity === 'function') window.clarity('event', eventName);
    } catch (e) { /* swallow */ }
  }

  // ----- Klaviyo subscribe -----
  function subscribeToKlaviyo(email, persona) {
    if (!KLAVIYO_LIST_ID) {
      console.warn('[mm-pq] No Klaviyo persona list ID configured; skipping subscribe');
      return Promise.resolve(null);
    }
    if (!KLAVIYO_KEY) {
      console.warn('[mm-pq] No Klaviyo public key configured');
      return Promise.resolve(null);
    }
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'persona_quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  persona: persona,
                  signup_source: 'persona_quiz',
                  quiz_completed_at: new Date().toISOString(),
                  discount_code_given: DISCOUNT_CODE,
                },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST_ID } } },
      },
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_KEY), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      mode: 'cors',
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok && res.status !== 202) {
        return res.text().then(function (t) { throw new Error('Klaviyo ' + res.status + ': ' + t); });
      }
      // Also fire a custom event for analytics
      try {
        fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(KLAVIYO_KEY), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
          mode: 'cors',
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                metric: { data: { type: 'metric', attributes: { name: 'Persona Quiz Completed' } } },
                profile: { data: { type: 'profile', attributes: { email: email } } },
                properties: {
                  persona: persona,
                  discount_code: DISCOUNT_CODE,
                },
              },
            },
          }),
        });
      } catch (e) { /* swallow */ }
      return res;
    });
  }

  // ----- UI state -----
  let modal = null;
  let currentPersona = null;
  let savedScrollY = 0;
  let lastFocusedBeforeOpen = null;

  // Focus-trap helper: collect visible, enabled focusables inside the open dialog.
  function getFocusable() {
    if (!modal) return [];
    const sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(modal.querySelectorAll(sel)).filter(function (el) {
      return !el.closest('[hidden]') && el.getClientRects().length > 0;
    });
  }

  // Lock page scroll while the popup is open. Avoid fixing the body in place:
  // on long PDPs that creates a huge offscreen body and iOS can expose it as a
  // white scroll area below the footer/sticky add-to-cart bar.
  function lockBodyScroll() {
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('mm-pq-scroll-lock');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  function unlockBodyScroll() {
    document.documentElement.classList.remove('mm-pq-scroll-lock');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    // Clean up fixed-body styles from older cached sessions.
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  function showStep(step) {
    if (!modal) return;
    modal.querySelectorAll('[data-mm-pq-step]').forEach(function (el) {
      el.setAttribute('hidden', '');
    });
    const target = modal.querySelector('[data-mm-pq-step="' + step + '"]');
    if (target) target.removeAttribute('hidden');
    // Update step indicator (just the number; "/ 3" is in a sibling span)
    const indicator = modal.querySelector('[data-mm-pq-step-indicator]');
    if (indicator) indicator.textContent = String(step);
    // Back button only visible on step 2
    const back = modal.querySelector('[data-mm-pq-back]');
    if (back) {
      if (step === 2) back.removeAttribute('hidden');
      else back.setAttribute('hidden', '');
    }
  }

  function openModal() {
    if (!modal) return;
    if (modal.classList.contains('is-open')) return;
    modal.classList.add('is-open');
    modal.removeAttribute('hidden');
    lockBodyScroll();
    writeStorage(STORAGE_SEEN, String(Date.now()));
    showStep(1);
    track('mm_persona_quiz_shown', {});
    // Remember what had focus so we can restore it on close (WCAG 2.4.3)
    lastFocusedBeforeOpen = document.activeElement;
    // Focus first interactive
    setTimeout(function () {
      const first = modal.querySelector('[data-mm-pq-persona]');
      if (first) first.focus();
    }, 100);
  }

  function closeModal(reason) {
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('hidden', '');
    unlockBodyScroll();
    // Return focus to whatever opened the popup (WCAG 2.4.3)
    if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
      lastFocusedBeforeOpen.focus();
    }
    lastFocusedBeforeOpen = null;
    if (reason === 'dismissed') {
      writeStorage(STORAGE_DISMISSED, String(Date.now()));
      track('mm_persona_quiz_dismissed', { persona: currentPersona || null });
    }
  }

  function bindModalHandlers() {
    if (!modal) return;

    // Close handlers
    modal.querySelectorAll('[data-mm-pq-close]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        // Real links (e.g. the "Comprar" CTA) must navigate, not just close the popup.
        if (el.tagName === 'A' && el.getAttribute('href')) return;
        e.preventDefault();
        closeModal('dismissed');
      });
    });
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal('dismissed');
    });
    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') { closeModal('dismissed'); return; }
      if (e.key === 'Tab') {
        const focusable = getFocusable();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !modal.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !modal.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Step 1: persona buttons
    modal.querySelectorAll('[data-mm-pq-persona]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        currentPersona = btn.getAttribute('data-mm-pq-persona');
        track('mm_persona_quiz_step_1_complete', { persona: currentPersona });
        showStep(2);
        // Pre-fill email if Mae & Mini knows it
        const emailInput = modal.querySelector('[data-mm-pq-email]');
        const customerEmail = (window.MMWishlistConfig && window.MMWishlistConfig.customerEmail) || '';
        if (emailInput && customerEmail) emailInput.value = customerEmail;
        setTimeout(function () { if (emailInput) emailInput.focus(); }, 100);
      });
    });

    // Step 2: email submit
    const form = modal.querySelector('[data-mm-pq-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        const emailInput = modal.querySelector('[data-mm-pq-email]');
        const submit = modal.querySelector('[data-mm-pq-submit]');
        const submitLabel = modal.querySelector('[data-mm-pq-submit-label]');
        const errorEl = modal.querySelector('[data-mm-pq-error]');
        const email = (emailInput && emailInput.value || '').trim();

        if (errorEl) errorEl.setAttribute('hidden', '');

        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (errorEl) { errorEl.textContent = 'Pon un correo válido'; errorEl.removeAttribute('hidden'); }
          return;
        }

        if (submit) submit.setAttribute('disabled', '');
        if (submitLabel) submitLabel.textContent = 'Enviando...';

        subscribeToKlaviyo(email, currentPersona).then(function () {
          writeStorage(STORAGE_COMPLETED, '1');
          track('mm_persona_quiz_completed', { persona: currentPersona, email_captured: true });
          showStep(3);
        }).catch(function (err) {
          console.warn('[mm-pq] subscribe failed', err);
          if (errorEl) { errorEl.textContent = 'No pudimos enviarte el código. Intenta de nuevo.'; errorEl.removeAttribute('hidden'); }
        }).then(function () {
          if (submit) submit.removeAttribute('disabled');
          if (submitLabel) submitLabel.textContent = 'Recibir mi código';
        });
      });
    }

    // Step 2: back button
    const backBtn = modal.querySelector('[data-mm-pq-back]');
    if (backBtn) backBtn.addEventListener('click', function (e) {
      e.preventDefault();
      showStep(1);
    });

    // Step 3: copy code button
    const copyBtn = modal.querySelector('[data-mm-pq-copy]');
    if (copyBtn) copyBtn.addEventListener('click', function () {
      const fallback = function () {
        const ta = document.createElement('textarea');
        ta.value = DISCOUNT_CODE; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch (e) {}
        document.body.removeChild(ta);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(DISCOUNT_CODE).catch(fallback);
      } else {
        fallback();
      }
      // Only update the label span so we don't wipe the icon
      const label = copyBtn.querySelector('[data-mm-pq-copy-label]') || copyBtn;
      const original = label.textContent;
      label.textContent = '✓ Copiado';
      setTimeout(function () { label.textContent = original; }, 1800);
      track('mm_persona_quiz_code_copied', { persona: currentPersona });
    });
  }

  function openFromSource(source) {
    track('mm_persona_quiz_trigger', { source: source });
    openModal();
  }

  // ----- Boot -----
  function applyConfigToDom() {
    // Inject discount percent into the headline highlight pill + code value so theme settings take effect
    const discountEl = modal.querySelector('.mm-pq__hl');
    if (discountEl) discountEl.textContent = DISCOUNT_PERCENT + '% OFF';
    const codeEl = modal.querySelector('.mm-pq__ticket-code');
    if (codeEl) codeEl.textContent = DISCOUNT_CODE;
  }

  function boot() {
    modal = document.querySelector('[data-mm-pq-modal]');
    if (!modal) {
      console.warn('[mm-pq] modal markup not found');
      return;
    }
    applyConfigToDom();
    bindModalHandlers();

    if (quizConfig.force === true) {
      writeStorage(STORAGE_SEEN, '');
      writeStorage(STORAGE_COMPLETED, '');
      writeStorage(STORAGE_DISMISSED, '');
    }

    const pendingSource = quizConfig.triggerSource || '';
    quizConfig.triggerSource = '';
    quizConfig.force = false;
    if (pendingSource) setTimeout(function () { openFromSource(pendingSource); }, 0);

    window.MMPersonaQuiz._ready = true;
  }

  // Expose minimal API for debugging + manual triggers
  window.MMPersonaQuiz = {
    _ready: false,
    open: function (source) { openFromSource(source || 'manual'); },
    close: function () { closeModal('manual'); },
    reset: function () {
      writeStorage(STORAGE_SEEN, '');
      writeStorage(STORAGE_COMPLETED, '');
      writeStorage(STORAGE_DISMISSED, '');
      console.log('[mm-pq] state reset, will fire again');
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
